import React, { useEffect, useRef } from 'react';

import { Progress, ProgressBar } from 'components';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter  } from '@cfa/react-components';
import { ReportsModel } from './ReportsModel';
import { useExportCSV } from './useExportCSV';
import { AsyncStatus } from 'enums';

interface IReportsExportCSVModalProps {
    model: ReportsModel,
    show: boolean,
    onHide: () => void,
}

export function ReportsExportCSVModal({ model, show, onHide }: IReportsExportCSVModalProps) {
    const csv = useExportCSV();
    const cancelCallbackRef = useRef<() => void>();

    useEffect(() => {
        return cancelExport;
    }, [])

    // If we close before the export finishes, cancel it.
    function cancelExport() {
        if (cancelCallbackRef.current) {
            cancelCallbackRef.current();
            cancelCallbackRef.current = undefined;
        }
    }

    function onHideInternal() {
        cancelExport()
        onHide();
    }

    function generateFilename(tableName: string): string {
        const maxChars = 15;
        const cleanedTableName = tableName
            .trim()
            // Replace any non alphanumeric characters
            .replace(/[^a-z0-9]/gi, '_')
            .slice(0, maxChars + 1);

        return `report_${cleanedTableName}.csv`
    }

    function onDownload() {
        if (csv.status !== AsyncStatus.Initial) return;

        const { cancel, resultPromise } = csv.generate(model.outFields, model.outRows);
        cancelCallbackRef.current = cancel;

        resultPromise.then((csv) => {
            cancelCallbackRef.current = undefined;

            // Store the data in a blob, then create a link to it.
            const blob = new Blob([csv], {type: 'text/csv'})
            const url = window.URL.createObjectURL(blob);
            const filename = generateFilename(model.activeTableId);

            // Click a hidden link to initiate the download.
            const a = document.createElement('a');
            a.setAttribute('style', 'display: none;');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a); // Required for FF
            a.click(); // Download the file
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            onHide();
        })
    }

    return (
        <Modal show={show} onClose={onHideInternal}>
            <ModalHeader>
                Export Results to CSV
            </ModalHeader>
            <ModalBody>
                <div className="row" style={{'marginTop': '0px'}}>
                    <div className="col">
                        This will export the results of the current report to a CSV file.
                    </div>
                </div>
                {csv.status !== AsyncStatus.Initial && (
                    <div className="row">
                        <div className="col">
                            <Progress>
                                <ProgressBar value={csv.progress} />
                            </Progress>
                        </div>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button variant="outlined" onClick={onHide}>
                    Close
                </Button>
                <Button onClick={onDownload} disabled={csv.status !== AsyncStatus.Initial}>
                    {csv.status === AsyncStatus.Initial && 'Download CSV'}
                    {csv.status !== AsyncStatus.Initial && 'Downloading...'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}